import { useState, useEffect } from 'react';
import { Link, NavLink as RouterNavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { logout } from '../store/authSlice';
import { useTheme } from '../context/ThemeContext';
import { 
  SparklesIcon,
  BoltIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';

export const NavigationBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const { darkMode } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const publicNavItems = [
    { to: '/about', label: 'About' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/login', label: 'Login' },
    { to: '/register', label: 'Get Started', highlight: true },
  ];

  // Different navigation items for free vs premium users
  const freeUserNavItems = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/quizzes', label: 'My Quizzes' },
    { to: '/quiz/create?type=manual', label: 'Create Quiz' },
    { to: '/quiz/create?type=ai', label: 'AI Quiz', highlight: true },
  ];

  const premiumUserNavItems = [
    { to: '/premium', label: 'Dashboard' },
    { to: '/quizzes', label: 'My Quizzes' },
    { to: '/statistics', label: 'Statistics' },
    { to: '/quiz/create?type=manual', label: 'Create Quiz' },
    { to: '/quiz/create?type=ai', label: 'AI Quiz', highlight: true },
  ];

  // Select appropriate nav items based on user status
  const authNavItems = user?.isPremium ? premiumUserNavItems : freeUserNavItems;

  // Profile menu items
  const profileMenuItems = [
    { to: '/profile', label: 'Profile', icon: <UserCircleIcon className="w-4 h-4 mr-2" /> },
    ...(user?.isPremium ? [{ to: '/settings', label: 'Settings', icon: <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg> }] : []),
    ...(user?.isPremium ? [] : [{ to: '/pricing', label: 'Upgrade to PRO', icon: <SparklesIcon className="w-4 h-4 mr-2 text-yellow-500" />, highlight: true }]),
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-lg border-b transition-all duration-300 ${
      isScrolled 
        ? darkMode 
          ? 'bg-gray-900/90 border-gray-700/50 shadow-lg shadow-gray-900/20' 
          : 'bg-white/90 border-gray-200/50 shadow-lg shadow-black/5'
        : darkMode
          ? 'bg-transparent border-transparent' 
          : 'bg-transparent border-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <motion.a
              href={user ? (user.isPremium ? '/premium' : '/dashboard') : '/'}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-600 to-indigo-700 flex items-center justify-center mr-2 shadow-md shadow-indigo-500/20">
                <BoltIcon className="w-6 h-6 text-white" />
              </div>
              <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                PowerQuizzer
                {user?.isPremium && (
                  <motion.span 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="ml-1 text-xs font-medium text-yellow-500 inline-flex items-center"
                  >
                    <SparklesIcon className="h-3 w-3 mr-0.5 animate-pulse" />
                    PRO
                  </motion.span>
                )}
              </span>
            </motion.a>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-1">
            {user ? (
              <>
                {authNavItems.map((item) => (
                  <RouterNavLink
                    key={item.to}
                    to={item.to}
                    onMouseEnter={() => setHoveredItem(item.to)}
                    onMouseLeave={() => setHoveredItem(null)}
                    className={({ isActive }: { isActive: boolean }) =>
                      `px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative group ${
                        item.highlight
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:shadow-md hover:shadow-indigo-500/20 hover:from-indigo-600 hover:to-purple-700'
                          : isActive 
                            ? darkMode 
                              ? 'text-indigo-400' 
                              : 'text-indigo-600' 
                            : darkMode 
                              ? 'text-gray-300 hover:text-indigo-400' 
                              : 'text-gray-700 hover:text-indigo-600'
                      }`
                    }
                  >
                    <span className="relative z-10">{item.label}</span>
                    {!item.highlight && (
                      <motion.div
                        className={`absolute inset-0 ${darkMode ? 'bg-gray-800' : 'bg-indigo-50'} rounded-lg -z-10 opacity-0 group-hover:opacity-100`}
                        layoutId="nav-highlight"
                        initial={false}
                        animate={{ opacity: hoveredItem === item.to ? 1 : 0 }}
                        transition={{ type: "spring", bounce: 0.2 }}
                      />
                    )}
                    {(!item.highlight && hoveredItem === item.to) && (
                      <motion.div
                        layoutId="nav-underline"
                        className={`absolute bottom-0 left-0 right-0 h-0.5 ${darkMode ? 'bg-indigo-400' : 'bg-indigo-600'}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                  </RouterNavLink>
                ))}
                
                {/* Profile Button with Dropdown */}
                <div className="relative ml-4">
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                      darkMode 
                        ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    } transition-colors duration-200`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold mr-2">
                      {user.username?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="hidden lg:block max-w-[100px] truncate">
                      {user.username || user.email?.split('@')[0]}
                    </span>
                    <svg className={`ml-1 h-4 w-4 transition-transform duration-200 ${isProfileMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  <AnimatePresence>
                    {isProfileMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className={`absolute right-0 mt-2 w-48 rounded-xl ${
                          darkMode 
                            ? 'bg-gray-800 border border-gray-700 shadow-lg shadow-black/30' 
                            : 'bg-white border border-gray-200 shadow-lg shadow-black/10'
                        } overflow-hidden`}
                      >
                        <div className={`px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Signed in as</p>
                          <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {user.email}
                          </p>
                        </div>
                        <div className="py-1">
                          {profileMenuItems.map((item) => (
                            <Link
                              key={item.to}
                              to={item.to}
                              onClick={() => setIsProfileMenuOpen(false)}
                              className={`flex items-center px-4 py-2 text-sm ${
                                item.highlight
                                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium'
                                  : darkMode 
                                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                              }`}
                            >
                              {item.icon}
                              {item.label}
                            </Link>
                          ))}
                          <button
                            onClick={() => {
                              handleLogout();
                              setIsProfileMenuOpen(false);
                            }}
                            className={`flex w-full items-center px-4 py-2 text-sm ${
                              darkMode 
                                ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Logout
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              <>
                {publicNavItems.map((item) => (
                  <RouterNavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }: { isActive: boolean }) =>
                      `block px-3 py-2 rounded-lg text-sm font-medium ${
                        item.highlight
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                          : isActive 
                            ? darkMode 
                              ? 'text-indigo-400 bg-gray-800' 
                              : 'text-indigo-600 bg-indigo-50' 
                            : darkMode 
                              ? 'text-gray-300 hover:text-indigo-400 hover:bg-gray-800' 
                              : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50'
                      }`
                    }
                  >
                    {item.label}
                  </RouterNavLink>
                ))}
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-lg ${
                darkMode 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`md:hidden overflow-hidden ${darkMode ? 'bg-gray-900 border-t border-gray-700' : 'bg-white border-t border-gray-200'}`}
          >
            <div className="px-4 py-3 space-y-1">
              {user ? (
                <>
                  <div className={`mb-4 p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'} flex items-center`}>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold mr-3">
                      {user.username?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {user.username || user.email?.split('@')[0]}
                      </p>
                      <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {user.email}
                      </p>
                    </div>
                    {user.isPremium && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-400 to-yellow-500 text-white">
                        <SparklesIcon className="h-3 w-3 mr-0.5" />
                        PRO
                      </span>
                    )}
                  </div>
                  
                  {authNavItems.map((item) => (
                    <RouterNavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsMenuOpen(false)}
                      className={({ isActive }: { isActive: boolean }) =>
                        `flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                          item.highlight
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                            : isActive 
                              ? darkMode 
                                ? 'text-indigo-400 bg-gray-800' 
                                : 'text-indigo-600 bg-indigo-50' 
                              : darkMode 
                                ? 'text-gray-300 hover:text-indigo-400 hover:bg-gray-800' 
                                : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50'
                        }`
                      }
                    >
                      {item.label}
                    </RouterNavLink>
                  ))}
                  
                  {profileMenuItems.map((item) => (
                    <RouterNavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsMenuOpen(false)}
                      className={({ isActive }: { isActive: boolean }) =>
                        `flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                          item.highlight
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                            : isActive 
                              ? darkMode 
                                ? 'text-indigo-400 bg-gray-800' 
                                : 'text-indigo-600 bg-indigo-50' 
                              : darkMode 
                                ? 'text-gray-300 hover:text-indigo-400 hover:bg-gray-800' 
                                : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50'
                        }`
                      }
                    >
                      {item.icon}
                      {item.label}
                    </RouterNavLink>
                  ))}
                  
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg ${
                      darkMode 
                        ? 'text-red-400 hover:text-red-300 hover:bg-gray-800' 
                        : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                    }`}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>
                </>
              ) : (
                <>
                  {publicNavItems.map((item) => (
                    <RouterNavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsMenuOpen(false)}
                      className={({ isActive }: { isActive: boolean }) =>
                        `block px-3 py-2 rounded-lg text-sm font-medium ${
                          item.highlight
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                            : isActive 
                              ? darkMode 
                                ? 'text-indigo-400 bg-gray-800' 
                                : 'text-indigo-600 bg-indigo-50' 
                              : darkMode 
                                ? 'text-gray-300 hover:text-indigo-400 hover:bg-gray-800' 
                                : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50'
                        }`
                      }
                    >
                      {item.label}
                    </RouterNavLink>
                  ))}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};